import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { UserPlus, Users, Search, X, MoreHorizontal } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { organizationService } from '../../services/organization.service';
import {
  Alert,
  Badge,
  Button,
  EmptyState,
  Field,
  Input,
  Modal,
  PageHeader,
  RoleBadge,
  Select,
  StatusBadge,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '../ui';
import { memberDisplayStatus } from './memberStatus';

const ROLES = ['OWNER', 'ADMIN', 'MEMBER'];
const STATUS_OPTIONS = ['ALL', 'ACTIVE', 'INVITED', 'SUSPENDED'];
const ROLE_LABELS = { OWNER: 'Owner', ADMIN: 'Admin', MEMBER: 'Member' };

const AVATAR_COLORS = [
  'bg-accent-100 text-accent-700',
  'bg-success-100 text-success-700',
  'bg-info-100 text-info-700',
  'bg-warning-100 text-warning-800',
  'bg-danger-100 text-danger-700',
];

const avatarColor = (seed) => AVATAR_COLORS[(seed?.charCodeAt(0) ?? 0) % AVATAR_COLORS.length];

const fmtDate = (v) =>
  v
    ? new Date(v).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
    : '—';

const fmtDateTime = (v) =>
  v
    ? new Date(v).toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '';

export const avatarInitials = (fullName, email, authId) => {
  if (fullName && fullName.trim()) {
    const parts = fullName.trim().split(/\s+/);
    if (parts.length > 1) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return parts[0].slice(0, 2).toUpperCase();
  }
  const s = email || authId || '?';
  return s.slice(0, 2).toUpperCase();
};

const MembersList = () => {
  const { user } = useAuth();
  const myRole = user?.profile?.org_role;
  const canManage = myRole === 'OWNER' || myRole === 'ADMIN';
  const myAuthId = user?.profile?.sub;

  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [query, setQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('ALL');
  const [statusFilter, setStatusFilter] = useState('ALL');

  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ email: '', role: 'MEMBER' });
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState(null);

  const [menuOpenFor, setMenuOpenFor] = useState(null);
  const [menuPosition, setMenuPosition] = useState(null);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [resendingId, setResendingId] = useState(null);
  const [toast, setToast] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await organizationService.listMembers();
      setMembers(Array.isArray(data) ? data : []);
    } catch {
      setError("We couldn't load your team. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const closeMenu = () => {
    setMenuOpenFor(null);
    setMenuPosition(null);
  };

  const toggleMenu = (m, e) => {
    e.stopPropagation();
    if (menuOpenFor === m.id) {
      closeMenu();
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    setMenuPosition({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
    setMenuOpenFor(m.id);
  };

  useEffect(() => {
    const onClick = () => closeMenu();
    if (menuOpenFor) {
      window.addEventListener('click', onClick);
      return () => window.removeEventListener('click', onClick);
    }
    return undefined;
  }, [menuOpenFor]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return members.filter((m) => {
      if (roleFilter !== 'ALL' && m.role !== roleFilter) return false;
      if (statusFilter !== 'ALL' && memberDisplayStatus(m) !== statusFilter) return false;
      if (!q) return true;
      return (
        (m.fullName || '').toLowerCase().includes(q) ||
        (m.email || '').toLowerCase().includes(q) ||
        (m.authId || '').toLowerCase().includes(q)
      );
    });
  }, [members, query, roleFilter, statusFilter]);

  const counts = useMemo(() => {
    const active = members.filter((m) => memberDisplayStatus(m) === 'ACTIVE').length;
    const invited = members.filter((m) => memberDisplayStatus(m) === 'INVITED').length;
    const suspended = members.filter((m) => m.status === 'SUSPENDED').length;
    const owners = members.filter((m) => m.role === 'OWNER' && m.status !== 'REMOVED').length;
    return { total: members.filter((m) => m.status !== 'REMOVED').length, active, invited, suspended, owners };
  }, [members]);

  const handleAdd = async (e) => {
    e.preventDefault();
    setAdding(true);
    setAddError(null);
    try {
      await organizationService.provisionMember(addForm);
      await load();
      setAddOpen(false);
      setAddForm({ email: '', role: 'MEMBER' });
    } catch (err) {
      setAddError(err?.response?.data?.message || 'Could not add member. Try again.');
    } finally {
      setAdding(false);
    }
  };

  const openEdit = (m) => {
    setEditing({ id: m.id, role: m.role });
    closeMenu();
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      await organizationService.updateMember(editing.id, { role: editing.role });
      await load();
      setEditing(null);
    } catch {
      // keep editor open on error
    } finally {
      setSaving(false);
    }
  };

  const changeStatus = async (m, status) => {
    closeMenu();
    try {
      await organizationService.updateMember(m.id, { status });
      await load();
    } catch {
      await load();
    }
  };

  const resendInvite = async (m) => {
    closeMenu();
    setResendingId(m.id);
    setToast(null);
    try {
      await organizationService.resendInvite(m.id);
      setToast({ tone: 'success', message: `Invitation resent to ${m.email || 'member'}.` });
    } catch {
      setToast({ tone: 'danger', message: 'Could not resend invitation. Try again.' });
    } finally {
      setResendingId(null);
    }
  };

  const roleOptionsForAdd = ROLES.filter((r) => myRole === 'OWNER' || r !== 'OWNER');

  return (
    <div>
      <PageHeader
        title="Team"
        description="People with access to this partner organization."
        actions={
          canManage && (
            <Button
              variant="primary"
              iconLeft={UserPlus}
              onClick={() => { setAddOpen(true); setAddError(null); }}
            >
              Add member
            </Button>
          )
        }
      />

      {!loading && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-5">
          {[
            { label: 'Members', value: counts.total },
            { label: 'Owners', value: counts.owners },
            { label: 'Active', value: counts.active },
            { label: 'Invited', value: counts.invited },
            { label: 'Suspended', value: counts.suspended },
          ].map((stat) => (
            <div key={stat.label} className="rounded-xl border border-ink-200 bg-white px-4 py-3">
              <p className="text-2xs font-semibold uppercase tracking-wider text-ink-400">{stat.label}</p>
              <p className="mt-1 text-xl font-semibold tracking-tight text-ink-900 tnum">{stat.value}</p>
            </div>
          ))}
        </div>
      )}

      {error && <Alert title="Couldn't load members" className="mb-4">{error}</Alert>}
      {toast && (
        <div className="mb-4">
          <Alert tone={toast.tone === 'danger' ? 'danger' : 'success'}>
            {toast.message}
          </Alert>
        </div>
      )}

      <div className="bg-white border border-ink-200 rounded-xl shadow-card overflow-hidden">
        <div className="flex items-center gap-3 px-4 py-3 border-b border-ink-100 flex-wrap">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-400" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name or email…"
              className="input pl-9"
            />
            {query && (
              <button type="button" onClick={() => setQuery('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-ink-400 hover:text-ink-700" aria-label="Clear search">
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <Select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} className="w-36">
            <option value="ALL">All roles</option>
            {ROLES.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
          </Select>
          <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="w-36">
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>{s === 'ALL' ? 'All statuses' : s.charAt(0) + s.slice(1).toLowerCase()}</option>
            ))}
          </Select>
          {!loading && (
            <span className="text-xs text-ink-500 tnum whitespace-nowrap">
              {filtered.length} of {members.filter((m) => m.status !== 'REMOVED').length}
            </span>
          )}
        </div>

        {loading ? (
          <div className="p-6 space-y-3">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="flex gap-4 items-center">
                <div className="skeleton h-9 w-9 rounded-full shrink-0" />
                <div className="flex-1 space-y-1.5">
                  <div className="skeleton h-4 w-48" />
                  <div className="skeleton h-3 w-32" />
                </div>
                <div className="skeleton h-5 w-16 rounded-md" />
                <div className="skeleton h-5 w-16 rounded-md" />
                <div className="skeleton h-4 w-20" />
              </div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          members.length === 0 ? (
            <EmptyState
              icon={Users}
              title="No members yet"
              description="Add your first team member to give them access."
              action={
                canManage && (
                  <Button variant="primary" iconLeft={UserPlus} onClick={() => setAddOpen(true)}>
                    Add member
                  </Button>
                )
              }
            />
          ) : (
            <EmptyState
              icon={Search}
              title="No matches"
              description="Try adjusting your search or filters."
              action={
                <Button variant="ghost" onClick={() => { setQuery(''); setRoleFilter('ALL'); setStatusFilter('ALL'); }}>
                  Clear filters
                </Button>
              }
            />
          )
        ) : (
        <Table>
          <THead>
            <TR>
              <TH>Member</TH>
              <TH className="w-28">Role</TH>
              <TH className="w-28">Status</TH>
              <TH className="w-36">Added</TH>
              {canManage && <TH className="w-16 text-right"><span className="sr-only">Actions</span></TH>}
            </TR>
          </THead>
          <TBody>
            {filtered.map((m) => {
              const isSelf = m.authId === myAuthId;
              const isOwner = m.role === 'OWNER';
              const isRemoved = m.status === 'REMOVED';
              const isEditing = editing?.id === m.id;
              const displayName = m.fullName?.trim() || m.email || 'Unknown member';
              const hasName = Boolean(m.fullName?.trim());

              return (
                <TR key={m.id} className={isRemoved ? 'opacity-50' : ''}>
                  <TD>
                    <div className="flex items-center gap-3 min-w-0">
                      <div className={`h-9 w-9 rounded-full flex items-center justify-center text-2xs font-semibold shrink-0 ${avatarColor(m.id || m.email)}`}>
                        {avatarInitials(m.fullName, m.email, m.authId)}
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 min-w-0">
                          <p className="font-medium text-ink-900 truncate">{displayName}</p>
                          {isSelf && <Badge tone="info">You</Badge>}
                        </div>
                        <p className="text-xs text-ink-500 truncate mt-0.5">
                          {hasName && m.email ? m.email : hasName ? '—' : 'Invite sent · hasn’t signed in'}
                        </p>
                      </div>
                    </div>
                  </TD>

                  <TD>
                    {isEditing ? (
                      <Select
                        value={editing.role}
                        onChange={(e) => setEditing({ ...editing, role: e.target.value })}
                        className="w-28 text-xs"
                        disabled={saving}
                      >
                        {ROLES.map((r) => (
                          <option key={r} value={r}>{ROLE_LABELS[r]}</option>
                        ))}
                      </Select>
                    ) : (
                      <RoleBadge role={m.role} />
                    )}
                  </TD>

                  <TD><StatusBadge status={memberDisplayStatus(m)} /></TD>

                  <TD>
                    <span className="text-sm text-ink-600" title={fmtDateTime(m.createdAt)}>
                      {fmtDate(m.createdAt)}
                    </span>
                  </TD>

                  {canManage && (
                    <TD className="text-right relative">
                      {isEditing ? (
                        <div className="flex justify-end gap-1.5">
                          <Button size="sm" variant="primary" onClick={saveEdit} disabled={saving}>
                            {saving ? 'Saving…' : 'Save'}
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => setEditing(null)}>
                            Cancel
                          </Button>
                        </div>
                      ) : isSelf || isRemoved ? null : (
                        <div className="relative inline-block">
                          <button
                            type="button"
                            className="p-1.5 rounded-md text-ink-400 hover:bg-ink-100 hover:text-ink-700"
                            onClick={(e) => toggleMenu(m, e)}
                            aria-label={`Open actions for ${displayName}`}
                          >
                            <MoreHorizontal className="h-4 w-4" />
                          </button>
                          {menuOpenFor === m.id && menuPosition && createPortal(
                            <div
                              className="fixed z-20 w-48 bg-white rounded-lg shadow-popover border border-ink-200 py-1 text-left"
                              style={{ top: menuPosition.top, right: menuPosition.right }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              {(!isOwner || myRole === 'OWNER') && (
                                <button type="button" className="w-full px-3 py-1.5 text-sm text-ink-700 hover:bg-ink-50 text-left" onClick={() => openEdit(m)}>
                                  Change role…
                                </button>
                              )}
                              {m.status === 'ACTIVE' && (
                                <button
                                  type="button"
                                  className="w-full px-3 py-1.5 text-sm text-ink-700 hover:bg-ink-50 text-left"
                                  onClick={() => resendInvite(m)}
                                  disabled={resendingId === m.id}
                                >
                                  {resendingId === m.id ? 'Resending…' : 'Resend invite'}
                                </button>
                              )}
                              {m.status === 'ACTIVE' && (
                                <button type="button" className="w-full px-3 py-1.5 text-sm text-warning-700 hover:bg-warning-50 text-left" onClick={() => changeStatus(m, 'SUSPENDED')}>
                                  Suspend access
                                </button>
                              )}
                              {m.status === 'SUSPENDED' && (
                                <button type="button" className="w-full px-3 py-1.5 text-sm text-success-700 hover:bg-success-50 text-left" onClick={() => changeStatus(m, 'ACTIVE')}>
                                  Reactivate
                                </button>
                              )}
                              {(!isOwner || myRole === 'OWNER') && (
                                <button type="button" className="w-full px-3 py-1.5 text-sm text-danger-700 hover:bg-danger-50 text-left border-t border-ink-100 mt-1 pt-1.5" onClick={() => changeStatus(m, 'REMOVED')}>
                                  Remove member
                                </button>
                              )}
                            </div>,
                            document.body,
                          )}
                        </div>
                      )}
                    </TD>
                  )}
                </TR>
              );
            })}
          </TBody>
        </Table>
        )}
      </div>

      {/* Add member modal */}
      <Modal
        open={addOpen}
        onClose={() => { setAddOpen(false); setAddError(null); setAddForm({ email: '', role: 'MEMBER' }); }}
        title="Add team member"
        description="We'll create their account and email a password setup link."
        footer={
          <>
            <Button variant="secondary" onClick={() => { setAddOpen(false); setAddError(null); setAddForm({ email: '', role: 'MEMBER' }); }}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleAdd} loading={adding} disabled={!addForm.email.trim()}>
              Add member
            </Button>
          </>
        }
      >
        {addError && <Alert tone="danger" className="mb-4">{addError}</Alert>}
        <div className="space-y-4">
          <Field label="Email address" required hint="They'll use this to sign in.">
            <Input
              type="email"
              required
              autoFocus
              value={addForm.email}
              onChange={(e) => setAddForm((p) => ({ ...p, email: e.target.value }))}
              placeholder="colleague@yourbank.com"
            />
          </Field>
          <Field label="Role" hint="OWNER — full control · ADMIN — manage team & settings · MEMBER — read-only">
            <Select value={addForm.role} onChange={(e) => setAddForm((p) => ({ ...p, role: e.target.value }))}>
              {roleOptionsForAdd.map((r) => (
                <option key={r} value={r}>{r.charAt(0) + r.slice(1).toLowerCase()}</option>
              ))}
            </Select>
          </Field>
        </div>
      </Modal>
    </div>
  );
};

export default MembersList;
